import React, { useState, useRef, useEffect, useMemo } from 'react';
// Import ReactQuill and its CSS
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css'; // Import Quill styles
// Keep Turndown for converting final HTML to Markdown
import TurndownService from 'turndown';
import { topColleges, applicationStatuses, statusColors } from '../colleges';
import collegeEssayPrompts from '../collegeEssayPrompts'; // Import the essay prompts
import { supabase } from '../supabaseClient';
import { useNavigate, useLocation } from 'react-router-dom'; // Import useNavigate and useLocation
// DOMPurify is no longer strictly needed for paste, but good for general safety if needed elsewhere
// import DOMPurify from 'dompurify';
// MarkdownIt is no longer needed here
// import MarkdownIt from 'markdown-it';

const DEBUG = true; // Set to false in production

function logDebug(message, data) {
  if (DEBUG) {
    console.log(`[DEBUG] ${message}`, data ? data : '');
  }
}

// --- Turndown Configuration (Focus on BR rule) ---
const turndownService = new TurndownService({
  headingStyle: 'atx',
  bulletListMarker: '-',
  codeBlockStyle: 'fenced',
  emDelimiter: '*',
  // Removed keepReplacement option
})
.addRule('explicitBr', { // Define BR rule first for higher precedence
    filter: 'br',
    replacement: function (content, node, options) {
        // Ensure it returns the standard Markdown hard break
        return '  \n';
    }
})
// Let Turndown handle paragraphs (<p>) with its default rules
// Keep default emphasis/strong handling
.addRule('emphasis', { filter: ['em', 'i'], replacement: (content) => '*' + content + '*' })
.addRule('strong', { filter: ['strong', 'b'], replacement: (content) => '**' + content + '**' });
// --- End Turndown ---

export default function SubmitForm() {
  // State for Quill's HTML content
  const [editorHtml, setEditorHtml] = useState('');
  const [wordCount, setWordCount] = useState(0); // ADDED: State for word count
  const [formData, setFormData] = useState({
    extra1: '',
    extra2: '',
    extra3: '',
    awards: '',
    collegeApplications: {},
    gpa: '',
    sat_score: '',
    act_score: '',
    ap_num_taken: '',
    ap_num_fives: '',
    class_year: '',
    specific_program: '',
  });
  // Ref to access the Quill instance
  const quillRef = useRef(null);
  const MAX_WORDS = 1000; // Define the limit
  const navigate = useNavigate(); // Initialize navigate
  const location = useLocation();
  const editModeParams = location.state || {};
  
  // Add state for edit mode and existing profile
  const [isEditMode, setIsEditMode] = useState(!!editModeParams.editMode);
  const [existingProfileId, setExistingProfileId] = useState(editModeParams.profileId || null);
  const [existingProfile, setExistingProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // New state for supplemental essays - change to support multiple schools
  const [supplementalEssays, setSupplementalEssays] = useState({});
  
  // Keep track of word counts for each supplemental essay - organized by school and essay
  const [supplementalWordCounts, setSupplementalWordCounts] = useState({});
  
  // Track which schools have been selected for supplemental essays
  const [selectedSchools, setSelectedSchools] = useState([]);
  
  // Currently active school in the supplemental essay section
  const [activeSupplementalSchool, setActiveSupplementalSchool] = useState('');
  
  // Refs for each supplemental essay editor - keep as an object but with numeric keys
  const supplementalRefs = useRef({});

  // Fetch existing profile data on component mount
  useEffect(() => {
    async function fetchExistingProfile() {
      setLoading(true);
      try {
        // Check if we're forcing a new submission (for developer mode) FIRST
        const forceNew = localStorage.getItem('forceNewSubmission');
        if (forceNew) {
          console.log("Dev Tool: forceNewSubmission flag detected. Forcing new submission mode.");
          
          // Get current user
          const { data: { user }, error: userError } = await supabase.auth.getUser();
          if (userError) throw userError;

          // Update user's submitted_profile status to false
          const { error: updateError } = await supabase
            .from('users')
            .update({ 
              submitted_profile: false,
              updated_at: new Date().toISOString()
            })
            .eq('id', user.id);

          if (updateError) {
            console.error("Failed to update user submission status:", updateError);
            throw updateError;
          }

          // Clear ALL relevant localStorage items
          localStorage.removeItem('forceNewSubmission');
          localStorage.removeItem('editProfileId');
          
          // Force new submission state
          setIsEditMode(false);
          setExistingProfile(null);
          setExistingProfileId(null);
          
          // Reset form data
          setFormData({
            extra1: '',
            extra2: '',
            extra3: '',
            awards: '',
            collegeApplications: {},
            gpa: '',
            sat_score: '',
            act_score: '',
            ap_num_taken: '',
            ap_num_fives: '',
            class_year: '',
            specific_program: '',
          });

          // Clear essay content and supplemental essays
          setEditorHtml('');
          setWordCount(0);
          setSupplementalEssays({});
          setSupplementalWordCounts({});
          setSelectedSchools([]);
          setActiveSupplementalSchool('');
          
          setLoading(false);
          console.log("Dev Tool: Form reset for new submission.");
          return; // Skip fetching existing profile data
        }

        // If not forcing new, proceed with normal profile fetching logic
        const { data: { user }, error: userError } = await supabase.auth.getUser();
        if (userError) throw userError;
        
        console.log("Fetching profile for user:", user.id);
        
        // If an explicit profile ID was provided (e.g., from clicking Edit on AccountPage)
        const profileIdFromStorage = localStorage.getItem('editProfileId');
        const effectiveProfileId = existingProfileId || profileIdFromStorage;

        if (profileIdFromStorage) {
          console.log(`Using profile ID from localStorage: ${profileIdFromStorage}`);
          // Only remove after we've used it and stored it in state
          if (effectiveProfileId) {
            localStorage.removeItem('editProfileId');
            console.log('Cleared profile ID from localStorage after capturing in state');
          }
        }

        let query = supabase.from('profiles').select('*');
        
        if (effectiveProfileId) {
           console.log(`Fetching profile by ID: ${effectiveProfileId}`);
           query = query.eq('id', effectiveProfileId).single();
        } else {
           console.log(`Fetching latest profile for user: ${user.id}`);
           query = query.eq('user_id', user.id)
                      .order('created_at', { ascending: false })
                      .limit(1);
        }
        
        const { data, error } = await query;
        
        if (error) {
          throw error;
        }
        
        let profileData;
        if (Array.isArray(data)) {
          profileData = data.length > 0 ? data[0] : null;
        } else {
          profileData = data; // For the .single() case
        }
        
        console.log("Profile query result:", profileData);
        
        if (profileData) {
          // User has an existing profile
          console.log("Found existing profile:", profileData.id);
          setExistingProfile(profileData);
          setIsEditMode(true);
          
          // Populate form data with existing values
          setFormData({
            extra1: profileData.extracurriculars?.[0] || '',
            extra2: profileData.extracurriculars?.[1] || '',
            extra3: profileData.extracurriculars?.[2] || '',
            awards: profileData.awards || '',
            collegeApplications: profileData.college_results || {},
            gpa: profileData.gpa?.toString() || '',
            sat_score: profileData.sat_score?.toString() || '',
            act_score: profileData.act_score?.toString() || '',
            ap_num_taken: profileData.ap_num_taken?.toString() || '',
            ap_num_fives: profileData.ap_num_fives?.toString() || '',
            class_year: profileData.class_year?.toString() || '',
            specific_program: profileData.special_program || '',
          });
          
          // Set essay content
          if (profileData.essay) {
            // Convert markdown to HTML for the editor
            const turndownService = new TurndownService();
            const html = turndownService.turndown(profileData.essay);
            setEditorHtml(html);
            
            // Calculate word count
            const words = profileData.essay.trim().split(/\s+/).filter(Boolean);
            setWordCount(words.length);
          }
          
          // Set supplemental essays if they exist
          if (profileData.supplemental_essays) {
            // Handle both old single-school format and new multi-school format
            if (profileData.supplemental_essays.college) {
              // Old format - single school
              const college = profileData.supplemental_essays.college;
              setSelectedSchools([college]);
              setActiveSupplementalSchool(college);
              
              const essays = {};
              const wordCounts = {};
              
              if (profileData.supplemental_essays.essays) {
                // Handle array format from old system
                profileData.supplemental_essays.essays.forEach((essayData) => {
                  const essayKey = `${college}_${essayData.index}`;
                  const turndownService = new TurndownService();
                  const html = turndownService.turndown(essayData.response);
                  essays[essayKey] = html;
                  
                  const words = essayData.response.trim().split(/\s+/).filter(Boolean);
                  wordCounts[essayKey] = words.length;
                });
              } else if (profileData.supplemental_essays.prompts) {
                // Handle prompt-based format
                Object.entries(profileData.supplemental_essays.prompts).forEach(([id, data]) => {
                  const essayKey = `${college}_${id}`;
                  const turndownService = new TurndownService();
                  const html = turndownService.turndown(data.response);
                  essays[essayKey] = html;
                  
                  const words = data.response.trim().split(/\s+/).filter(Boolean);
                  wordCounts[essayKey] = words.length;
                });
              }
              
              setSupplementalEssays(essays);
              setSupplementalWordCounts(wordCounts);
            } else if (profileData.supplemental_essays.schools) {
              // New format - multiple schools
              const schools = Object.keys(profileData.supplemental_essays.schools);
              setSelectedSchools(schools);
              setActiveSupplementalSchool(schools[0] || '');
              
              const essays = {};
              const wordCounts = {};
              
              Object.entries(profileData.supplemental_essays.schools).forEach(([school, schoolData]) => {
                if (schoolData.essays) {
                  Object.entries(schoolData.essays).forEach(([essayId, essayContent]) => {
                    const essayKey = `${school}_${essayId}`;
                    const turndownService = new TurndownService();
                    const html = turndownService.turndown(essayContent);
                    essays[essayKey] = html;
                    
                    const words = essayContent.trim().split(/\s+/).filter(Boolean);
                    wordCounts[essayKey] = words.length;
                  });
                }
              });
              
              setSupplementalEssays(essays);
              setSupplementalWordCounts(wordCounts);
            }
          }
        } else {
          console.log("No existing profile found");
          setIsEditMode(false);
          setExistingProfile(null);
        }
      } catch (error) {
        console.error("Error fetching profile:", error);
        // Reset to non-edit mode on error maybe?
        setIsEditMode(false);
        setExistingProfile(null);
        setExistingProfileId(null);
      } finally {
        setLoading(false);
      }
    }
    
    fetchExistingProfile();
  }, [existingProfileId]); // Add existingProfileId as a dependency

  // Update the useEffect hook that checks for localStorage profileId
  useEffect(() => {
    // Check for profileId in localStorage (reliable across navigations/refreshes)
    const storedProfileId = localStorage.getItem('editProfileId');
    if (storedProfileId) {
      console.log(`Found profile ID in localStorage: ${storedProfileId}`);
      setExistingProfileId(storedProfileId);
      setIsEditMode(true);
      // Keep the ID in localStorage until the form successfully loads the profile
      // This will be cleared in the fetchExistingProfile function
    }
  }, []);

  // Handler for Quill editor changes
  const handleEditorChange = (content, delta, source, editor) => {
    // Get the full text *after* the change has occurred internally in Quill
    const text = editor.getText();
    // Calculate word count from the potentially new text
    const words = text.trim().split(/\s+/).filter(Boolean);
    const currentWordCount = words.length;

    // Check if the change was made by the user and exceeds the limit
    if (source === 'user' && currentWordCount > MAX_WORDS) {
      // User typed/pasted text that exceeded the limit
      const quill = quillRef.current?.getEditor();
      if (quill) {
        // Revert the last change made by the user
        quill.history.undo();
        // The editor's content is now reverted.
        // We don't update the React state (editorHtml, wordCount)
        // because the change was invalid. The word count display
        // will remain at its last valid value until the next valid change.
        console.log("Word limit reached. Change reverted.");
      }
    } else {
      // If the change is valid (within limit or not from user like API changes), update React state
      setEditorHtml(content); // Update HTML state with the latest valid content
      setWordCount(currentWordCount); // Update word count state
    }
  };

  // --- Redaction using Quill API ---
  const handleRedactClick = () => {
    const quill = quillRef.current?.getEditor(); // Get the Quill editor instance
    if (!quill) return;

    const range = quill.getSelection(); // Get current selection range
    if (!range || range.length === 0) {
      alert("Please select the text in the essay box you want to redact first.");
      return;
    }

    // Get the selected text to check if it's already redacted (simple check)
    const selectedText = quill.getText(range.index, range.length);
    if (selectedText.includes('[REDACTED]')) {
       alert("The selected text appears to already contain a redacted section.");
       return;
    }

    // Replace the selected text with the redaction marker
    // We format it slightly (e.g., gray background) using Quill's format API
    // Note: Direct background color formatting might not convert well to Markdown.
    // A simpler approach is just inserting the text.
    quill.deleteText(range.index, range.length); // Delete the selected text
    quill.insertText(range.index, '[REDACTED]', {
        // Optional: Apply some visual cue if desired, but it won't be in the final Markdown
        // 'background': '#cccccc',
        // 'color': '#cccccc'
    }); // Insert the marker text

    // Move cursor after the inserted text
    quill.setSelection(range.index + '[REDACTED]'.length, 0);

    // Update the main state (editorHtml) after the change
    // Get the updated HTML content from Quill
    setEditorHtml(quill.root.innerHTML);
  };


  // --- Other handlers ---
  const handleChange = (e) => { 
      const { name, value } = e.target; 
      const newFormData = { ...formData, [name]: value };
      setFormData(newFormData); 
  };
  const handleCollegeClick = (collegeName) => {
    setFormData(prev => {
      const currentStatus = prev.collegeApplications[collegeName];
      let nextStatusIndex = -1; // Start with index -1 (no status)

      if (currentStatus) {
        nextStatusIndex = applicationStatuses.indexOf(currentStatus);
      }

      // Increment index to get the next status
      nextStatusIndex++;

      let nextStatus = null;
      if (nextStatusIndex < applicationStatuses.length) {
        // If the incremented index is still within bounds, set the next status
        nextStatus = applicationStatuses[nextStatusIndex];
      }
      // Else: If index went out of bounds (was last status 'Applied'), nextStatus remains null (deselect)

      const newApplications = { ...prev.collegeApplications };

      // Special logic: If setting to 'Committed', deselect 'Committed' from all other colleges
      if (nextStatus === 'Committed') {
          Object.keys(newApplications).forEach(college => {
              if (newApplications[college] === 'Committed' && college !== collegeName) {
                  // Decide how to handle existing commit: Deselect for now
                  delete newApplications[college];
              }
          });
      }

      if (nextStatus) {
        newApplications[collegeName] = nextStatus;
      } else {
        // Deselect: remove from object
        delete newApplications[collegeName];
      }

      return {
        ...prev,
        collegeApplications: newApplications,
      };
    });
  };
  const getLogoPath = (collegeName) => {
    const logoMap = {
      'Harvard': 'Harvard_University_shield.png',
      'Yale': 'Yale-logo.png',
      'Princeton': 'princetonlogo.png',
      'UPenn': 'pennlogo.png',
      'Columbia': 'Columbialogo.png',
      'Brown': 'brownlogo.png',
      'Dartmouth': 'Dartmouth-Symbol.png',
      'Cornell': 'cornelllogo.png',
      'Stanford': 'Stanfordlogo.png',
      'MIT': 'MIT-Emblem.png',
      'Caltech': 'caltechlogo.png',
      'UChicago': 'uchicagologo.png',
      'Northwestern': 'Northwestern-Wildcats-Logo-1981.png',
      'Duke': 'dukelogo.png',
      'Johns Hopkins': 'JHUlogo.png',
      'Vanderbilt': 'vanderbiltlogo.png',
      'UC Berkeley': 'ucberkeleylogo.png',
      'UCLA': 'uclalogo.png',
      'UMichigan': 'uofmlogo.png',
      'UVA': 'UVAlogo.png',
      'UNC Chapel Hill': 'unclogo.png',
      'Georgia Tech': 'georgiatechlogo.png',
      'NYU': 'nyulogo.png',
      'CMU': 'CMUlogo.png',
      'USC': 'USC_logo.png',
      'WashU': 'Washington_University_logo.png',
      'Rice': 'Rice_logo.png',
      'Notre Dame': 'Notre_dame_logo.png',
      'Northeastern': 'Northeastern_logo.png',
      'Georgetown': 'Georgetown_logo.png'
    };
    const fileName = logoMap[collegeName];
    // console.log(`Mapped path for ${collegeName}: /logos/${fileName}`); // Optional debug log
    return fileName ? `/logos/${fileName}` : null; // Return null if not in map
  };
  const getCollegeInitials = (collegeName) => {
    // Basic initials logic, adjust as needed
    return collegeName.split(' ').map(word => word[0]).join('').substring(0, 3).toUpperCase();
  };


  // --- Submit Handler (with Manual Paragraph Break Insertion) ---
  const handleSubmit = async (e) => {
    e.preventDefault();
    const isEditorEmpty = !editorHtml || editorHtml === '<p><br></p>';
    // Use the simplified turndownService instance
    let rawMarkdown = isEditorEmpty ? '' : turndownService.turndown(editorHtml);
    console.log("Raw Turndown:", JSON.stringify(rawMarkdown)); // Debug raw output

    // --- Manual Post-Processing ---
    // 1. Protect existing hard breaks (add a temporary placeholder if needed, although regex might handle it)
    //    Let's assume the '  \n' from the rule is preserved for now.

    // 2. Ensure paragraph breaks: Replace single newlines that are likely paragraph separators
    //    This regex looks for a newline `\n` that is NOT preceded by two spaces `(?<! {2})`
    //    and IS NOT immediately followed by another newline `(?!\n)`.
    //    Replace such single newlines with a double newline `\n\n`.
    let processedMarkdown = rawMarkdown.replace(/(?<! {2})\n(?!\n)/g, '\n\n');

    // 3. Consolidate any excess newlines (3 or more) resulting from step 2 or original conversion
    processedMarkdown = processedMarkdown.replace(/\n{3,}/g, '\n\n');

    // 4. Trim ONLY leading/trailing NEWLINES from the entire string
    const cleanedMarkdown = processedMarkdown.replace(/^\n+|\n+$/g, '');

    console.log("Cleaned Markdown (Post-Processed):", JSON.stringify(cleanedMarkdown)); // Debug final output

    // Use trim() *only* for the validation check
    if (!cleanedMarkdown.trim()) {
      alert('Please fill in the essay field.');
      return;
    }

    // Check other required fields (ONLY check extra1, class_year, and collegeApplications)
    if (Object.keys(formData.collegeApplications).length === 0) {
        // Update alert message slightly to reflect which fields are truly required
        alert('Please add at least one college application result.');
        return;
    }

    try {
      // Capture any unsaved essay content from all schools before processing
      captureAllEssayContent();
      
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError || !user) {
        alert('Login required.');
        logDebug("Auth error or no user found.");
        return;
      }
      
      // --- Process supplemental essays to markdown (updated for multi-school essays) ---
      const processedSupplementalEssays = selectedSchools.length > 0 ? {
        schools: {}
      } : null;

      // Process the essays for each selected school
      if (selectedSchools.length > 0) {
        // Get current content from both state and live editors
        const currentSupplementalEssays = { ...supplementalEssays };
        
        // Update with current editor content for all editors (prioritize live content)
        selectedSchools.forEach(school => {
          for (let i = 1; i <= 5; i++) {
            const essayKey = `${school}_essay_${i}`;
            const quillEditor = supplementalRefs.current[essayKey]?.getEditor();
            if (quillEditor) {
              const currentContent = quillEditor.root.innerHTML;
              if (currentContent && currentContent !== '<p><br></p>') {
                currentSupplementalEssays[essayKey] = currentContent;
                console.log(`Using live editor content for ${essayKey}, length: ${currentContent.length}`);
              }
            } else if (currentSupplementalEssays[essayKey]) {
              console.log(`Using state content for ${essayKey}, length: ${currentSupplementalEssays[essayKey].length}`);
            }
          }
        });
        
        selectedSchools.forEach(school => {
          processedSupplementalEssays.schools[school] = {
            essays: {}
          };
          
          // Find all essays for this school using the updated content
          Object.keys(currentSupplementalEssays).forEach(essayKey => {
            if (essayKey.startsWith(`${school}_`)) {
              const essayId = essayKey.substring(school.length + 1);
              const html = currentSupplementalEssays[essayKey];
              
              if (html && html !== '<p><br></p>') {
                let rawSuppMarkdown = turndownService.turndown(html);
                let processedSuppMarkdown = rawSuppMarkdown.replace(/(?<! {2})\n(?!\n)/g, '\n\n');
                processedSuppMarkdown = processedSuppMarkdown.replace(/\n{3,}/g, '\n\n');
                const cleanedSuppMarkdown = processedSuppMarkdown.replace(/^\n+|\n+$/g, '');
                
                processedSupplementalEssays.schools[school].essays[essayId] = cleanedSuppMarkdown;
              }
            }
          });
        });
        
        // Debug: Log what supplemental essays are being processed
        console.log("Processing supplemental essays for schools:", selectedSchools);
        console.log("Final processedSupplementalEssays:", JSON.stringify(processedSupplementalEssays, null, 2));
      }
      
      // Prepare submission data using EXACT database column names
      const submissionData = {
        essay: cleanedMarkdown,
        extracurriculars: [formData.extra1, formData.extra2, formData.extra3].filter(Boolean),
        awards: formData.awards,
        college_results: formData.collegeApplications, 
        gpa: formData.gpa ? parseFloat(formData.gpa) : null, 
        sat_score: parseInt(formData.sat_score) || null,
        act_score: parseInt(formData.act_score) || null,
        ap_num_taken: parseInt(formData.ap_num_taken) || null,
        ap_num_fives: parseInt(formData.ap_num_fives) || null,
        class_year: formData.class_year ? parseInt(formData.class_year) : null,
        special_program: formData.specific_program, 
        supplemental_essays: processedSupplementalEssays,
        updated_at: new Date().toISOString() // Explicitly set updated_at
      };
      
      // Use explicit profile ID or fall back to the existing profile ID
      const profileIdToUpdate = existingProfileId || (existingProfile && existingProfile.id);
      
      if (isEditMode && profileIdToUpdate) {
        logDebug("Update mode active for profile ID:", profileIdToUpdate);
        console.log("Updating profile with ID:", profileIdToUpdate);
        
        // Create a clean copy of submission data WITHOUT user_id for updates
        // user_id should NEVER change on updates and might trigger RLS policy failures
        const updateData = {...submissionData};
        delete updateData.user_id; // Remove user_id for update operations
        
        // Add detailed logging for debugging
        console.log("Update data:", JSON.stringify(updateData, null, 2));

        // Update existing profile with explicit error handling
        try {
          const { data: updateData, error: updateError } = await supabase
            .from('profiles')
            .update(updateData)
            .eq('id', profileIdToUpdate)
            .select();

          if (updateError) {
            console.error("Supabase Update Error:", updateError);
            alert(`Update failed: ${updateError.message}. Check console for details.`);
            return;
          }

          console.log("Profile update successful:", updateData);
          
          // Navigate to success page
          navigate('/submit-success', { 
            state: { 
              isEdit: true, 
              profileData: updateData[0],
              message: 'Your profile has been updated successfully!'
            } 
          });
        } catch (err) {
          console.error("Unexpected error during update:", err);
          alert(`Update failed with an unexpected error. Please check the console.`);
        }
      } else if (!isEditMode) {
        // Insert requires user_id
        submissionData.user_id = user.id; 
        logDebug("Create mode active. Inserting new profile with data:", submissionData);
        const { data: insertData, error: insertError } = await supabase
          .from('profiles')
          .insert([submissionData])
          .select();

        if (insertError) {
          console.error("Supabase Insert Error:", insertError);
          logDebug("Insert failed. Error:", insertError);
          alert(`Submit failed: ${insertError.message}.`);
          return;
        }
        
        logDebug("Insert successful. Response data:", insertData);

        // Update user status after successful insert
        const { error: statusUpdateError } = await supabase
          .from('users')
          .update({ submitted_profile: true, updated_at: new Date().toISOString() })
          .eq('id', user.id);
          
        if (statusUpdateError) {
          console.error("Failed to update user status after insert:", statusUpdateError);
        }
        
        // Navigate on successful insert
        navigate('/submit-success', { 
          state: { 
            isEdit: false, 
            message: 'Profile submitted successfully!' 
          } 
        });

      } else {
         // Handle unexpected state
         logDebug("Error: Edit mode active but profileIdToUpdate is missing.", { profileIdToUpdate });
         alert("Cannot update profile. Profile data is missing. Please try logging out and back in.");
         return;
      }

    } catch (error) {
      console.error("Submit error (catch block):", error);
      logDebug("An unexpected error occurred during submit:", error);
      alert(`Error ${isEditMode ? "updating" : "submitting"} profile. Please check the console and try again.`);
    }
  };

   // --- Quill Modules & Formats ---
   const quillModules = {
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline'], // Simplified toolbar
      [{'list': 'ordered'}, {'list': 'bullet'}],
      ['link'], ['clean']
    ],
     clipboard: {
       // Tweak paste behavior if needed, but defaults are often good
       matchVisual: false, // Recommended setting
     }
   };
   const quillFormats = [ // Whitelist formats
    'header', 'bold', 'italic', 'underline', 'strike', 'blockquote',
    'list', 'bullet', 'link',
    // Ensure 'background' and 'color' are NOT whitelisted if you don't want them saved
   ];

  // Update handler for supplemental essay changes
  const handleSupplementalChange = (school, essayId) => {
    const wordLimit = 750; // Fixed 750 word limit for all supplemental essays

    return (content, delta, source, editor) => {
      const text = editor.getText();
      const words = text.trim().split(/\s+/).filter(Boolean);
      const currentWordCount = words.length;
      const essayKey = `${school}_${essayId}`;
      
      // Check if the change exceeds the word limit
      if (source === 'user' && currentWordCount > wordLimit) {
        // Revert the change if it exceeds the word limit
        const quill = supplementalRefs.current[essayKey]?.getEditor();
        if (quill) {
          quill.history.undo();
          console.log(`Word limit (${wordLimit}) reached for essay ${essayKey}. Change reverted.`);
        }
      } else {
        // Update the essay content and word count
        setSupplementalEssays(prev => ({
          ...prev,
          [essayKey]: content
        }));
        
        setSupplementalWordCounts(prev => ({
          ...prev,
          [essayKey]: currentWordCount
        }));
      }
    };
  };

  // Add a new handler function for redacting supplemental essays text
  const handleSupplementalRedactClick = (school, essayId) => {
    return () => {
      const essayKey = `${school}_${essayId}`;
      const quill = supplementalRefs.current[essayKey]?.getEditor();
      if (!quill) return;

      const range = quill.getSelection();
      if (!range || range.length === 0) {
        alert("Please select the text in the essay box you want to redact first.");
        return;
      }

      // Check if already redacted
      const selectedText = quill.getText(range.index, range.length);
      if (selectedText.includes('[REDACTED]')) {
        alert("The selected text appears to already contain a redacted section.");
        return;
      }

      // Replace with redacted text
      quill.deleteText(range.index, range.length);
      quill.insertText(range.index, '[REDACTED]', {});
      quill.setSelection(range.index + '[REDACTED]'.length, 0);

      // Update essay content in state
      const updatedContent = quill.root.innerHTML;
      setSupplementalEssays(prev => ({
        ...prev,
        [essayKey]: updatedContent
      }));
    };
  };

  // Function to capture current essay content from currently active school only
  const captureCurrentEssayContent = () => {
    console.log("Capturing current essay content for active school:", activeSupplementalSchool);
    
    // Only capture content for the currently active school to avoid cross-contamination
    if (activeSupplementalSchool) {
      for (let i = 1; i <= 5; i++) {
        const essayKey = `${activeSupplementalSchool}_essay_${i}`;
        const quillEditor = supplementalRefs.current[essayKey]?.getEditor();
        if (quillEditor) {
          const currentContent = quillEditor.root.innerHTML;
          const existingContent = supplementalEssays[essayKey];
          
          // Only update if there's meaningful content that's different from what we have
          if (currentContent && currentContent !== '<p><br></p>' && currentContent !== existingContent) {
            console.log(`Capturing new content for ${essayKey}:`, {
              had_existing: !!existingContent,
              new_content_length: currentContent.length,
              first_50_chars: currentContent.substring(0, 50)
            });
            
            setSupplementalEssays(prev => ({
              ...prev,
              [essayKey]: currentContent
            }));
            
            // Update word count too
            const text = quillEditor.getText();
            const words = text.trim().split(/\s+/).filter(Boolean);
            setSupplementalWordCounts(prev => ({
              ...prev,
              [essayKey]: words.length
            }));
          }
        }
      }
    }
  };

  // Function to capture all essay content before submission
  const captureAllEssayContent = () => {
    console.log("Capturing all essay content for submission");
    
    // Capture from all schools, but be careful about which editors exist
    selectedSchools.forEach(school => {
      for (let i = 1; i <= 5; i++) {
        const essayKey = `${school}_essay_${i}`;
        const quillEditor = supplementalRefs.current[essayKey]?.getEditor();
        if (quillEditor) {
          const currentContent = quillEditor.root.innerHTML;
          const existingContent = supplementalEssays[essayKey];
          
          // Only update if there's meaningful content that's different from what we have
          if (currentContent && currentContent !== '<p><br></p>' && currentContent !== existingContent) {
            console.log(`Capturing content for submission ${essayKey}:`, {
              had_existing: !!existingContent,
              new_content_length: currentContent.length
            });
            
            setSupplementalEssays(prev => ({
              ...prev,
              [essayKey]: currentContent
            }));
          }
        }
      }
    });
  };

  // Function to add a school for supplemental essays
  const addSchoolForEssays = (school) => {
    // Capture current content before switching schools
    captureCurrentEssayContent();
    
    if (!selectedSchools.includes(school)) {
      setSelectedSchools(prev => [...prev, school]);
      setActiveSupplementalSchool(school);
    } else {
      setActiveSupplementalSchool(school);
    }
  };

  // Function to remove a school from supplemental essays
  const removeSchoolFromEssays = (schoolToRemove) => {
    setSelectedSchools(prev => prev.filter(school => school !== schoolToRemove));
    
    // Remove all essays for this school
    setSupplementalEssays(prev => {
      const newEssays = { ...prev };
      Object.keys(newEssays).forEach(key => {
        if (key.startsWith(`${schoolToRemove}_`)) {
          delete newEssays[key];
        }
      });
      return newEssays;
    });
    
    // Remove all word counts for this school
    setSupplementalWordCounts(prev => {
      const newCounts = { ...prev };
      Object.keys(newCounts).forEach(key => {
        if (key.startsWith(`${schoolToRemove}_`)) {
          delete newCounts[key];
        }
      });
      return newCounts;
    });
    
    // Update active school
    const remainingSchools = selectedSchools.filter(school => school !== schoolToRemove);
    setActiveSupplementalSchool(remainingSchools[0] || '');
  };

  return (
    <div className="submit-form fade-in">
      <div className="form-header-main">
        <h2>{isEditMode ? 'Edit Your Profile' : 'Submit Your Profile'}</h2>
        <p>{isEditMode 
            ? 'Update your application details and add supplemental essays.' 
            : 'Share your application details anonymously to get ranked.'}
        </p>
      </div>

      {loading ? (
        <div className="loading-container">
          <span className="spinner"></span>
          <p>Loading your profile data...</p>
        </div>
      ) : (
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>
              Main Personal Essay: <span className="required">*</span>
            </label>

            <div className="redact-controls">
              <button
                 type="button"
                 onClick={handleRedactClick}
                 className="redact-button"
                 title="Redact selected text"
              >
                 Redact Selection
              </button>
              <p className="redact-helper-text">
                 Select text in the essay box below, then click the button to redact it.
              </p>
            </div>

            <ReactQuill
              ref={quillRef}
              theme="snow"
              value={editorHtml}
              onChange={handleEditorChange}
              modules={quillModules}
              formats={quillFormats}
              placeholder="Paste or type Personal Statement..."
              className="quill-editor-container"
            />

            <div className={`word-count ${wordCount > MAX_WORDS ? 'limit-exceeded' : ''}`}>
              Word Count: {wordCount} / {MAX_WORDS}
            </div>

          </div>

          <div className="form-group">
            <label>Top Extracurriculars (Optional)</label>
            <input
              type="text"
              name="extra1"
              placeholder="Extracurricular 1 (Optional, Max 250 chars)"
              value={formData.extra1}
              onChange={handleChange}
              className="styled-input"
              maxLength="250"
            />
            <input
              type="text"
              name="extra2"
              placeholder="Extracurricular 2 (Optional, Max 250 chars)"
              value={formData.extra2}
              onChange={handleChange}
              style={{marginTop: '0.5rem'}}
              className="styled-input"
              maxLength="250"
            />
            <input
              type="text"
              name="extra3"
              placeholder="Extracurricular 3 (Optional, Max 250 chars)"
              value={formData.extra3}
              onChange={handleChange}
              style={{marginTop: '0.5rem'}}
              className="styled-input"
              maxLength="250"
            />
          </div>

          <div className="form-group">
            <label>Academic Stats:</label>
            <div className="stats-grid">
              <div className="stat-field">
                <label htmlFor="class_year">College Class Year (Optional):</label>
                <div className="select-wrapper">
                  <select id="class_year" name="class_year" value={formData.class_year} onChange={handleChange} className="styled-select">
                    <option value="">Unknown / not published</option>
                    {Array.from({ length: 81 }, (_, i) => 2030 - i).map(year => <option key={year} value={year}>{year}</option>)}
                  </select>
                </div>
              </div>
              <div className="stat-field">
                <label htmlFor="gpa">GPA (Unweighted):</label>
                <input type="number" step="0.01" id="gpa" name="gpa" placeholder="e.g., 3.90" value={formData.gpa} onChange={handleChange} className="styled-input" />
              </div>
              <div className="stat-field">
                <label htmlFor="sat_score">SAT Score:</label>
                <input type="number" id="sat_score" name="sat_score" placeholder="e.g., 1550" value={formData.sat_score} onChange={handleChange} className="styled-input" />
              </div>
              <div className="stat-field">
                <label htmlFor="act_score">ACT Score:</label>
                <input type="number" id="act_score" name="act_score" placeholder="e.g., 35" value={formData.act_score} onChange={handleChange} className="styled-input" />
              </div>
              <div className="stat-field">
                <label htmlFor="ap_num_taken"># APs/IBs Taken:</label>
                <input type="number" id="ap_num_taken" name="ap_num_taken" placeholder="e.g., 10" value={formData.ap_num_taken} onChange={handleChange} className="styled-input" />
              </div>
              <div className="stat-field">
                <label htmlFor="ap_num_fives"># APs Scored 5 / IBs Scored 7:</label>
                <input type="number" id="ap_num_fives" name="ap_num_fives" placeholder="e.g., 8" value={formData.ap_num_fives} onChange={handleChange} className="styled-input" />
              </div>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="awards">Top 3 Awards/Honors:</label>
            <input
              type="text"
              id="awards"
              name="awards"
              placeholder="e.g., USAMO Qualifier, Scholastic Gold Key"
              value={formData.awards}
              onChange={handleChange}
              className="styled-input"
            />
          </div>

          <div className="form-group">
            <label htmlFor="specific_program">Specific Program/Major Admitted To:</label>
            <input
              type="text"
              id="specific_program"
              name="specific_program"
              placeholder="e.g., EECS, Wharton, Robertson Scholars"
              value={formData.specific_program}
              onChange={handleChange}
              className="styled-input"
            />
          </div>

          <div className="form-group">
            <label>
              Application Outcomes (Click to cycle: Rejected &rarr; Accepted &rarr; Waitlisted &rarr; Committed &rarr; Deselect) <span className="required">*</span>
            </label>
            <div className="college-selection-grid">
              {topColleges.map(college => {
                const status = formData.collegeApplications[college];
                const buttonStyle = status
                  ? { backgroundColor: statusColors[status], color: 'white', borderColor: statusColors[status] }
                  : {};

                const logoPath = getLogoPath(college);

                return (
                  <button
                    type="button"
                    key={college}
                    className={`college-button ${status ? 'selected' : ''}`}
                    onClick={() => handleCollegeClick(college)}
                    style={buttonStyle}
                    data-college={college}
                  >
                    <div className="college-logo-container">
                      {logoPath ? (
                        <img
                          src={logoPath}
                          alt={`${college} logo`}
                          className="college-logo"
                          onError={(e) => {
                            const fallback = e.target.nextElementSibling;
                            if (fallback) fallback.style.display = 'flex';
                            e.target.style.display = 'none';
                            e.target.onerror = null;
                          }}
                        />
                      ) : null }
                      <div className="logo-fallback" style={{ display: logoPath ? 'none' : 'flex' }}>
                        {getCollegeInitials(college)}
                      </div>
                    </div>
                    <span className="college-name">{college}</span>
                    <div className="college-status-container">
                      <span className="college-status">({status || ''})</span>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Supplemental Essays Section - Multi-School Support */}
          <div className="form-group">
            <div className="form-college-selector">
              <label>Add Supplemental Essays (Optional)</label>
              <p className="supplemental-helper">
                Add supplemental essays for schools you applied to. This allows you to participate in college-specific arenas and showcase your strongest applications.
              </p>
              
              {Object.keys(formData.collegeApplications).length > 0 && (
                <div className="college-selection-for-essays">
                  <label>Add supplemental essays for a school:</label>
                  <select
                    value=""
                    onChange={(e) => {
                      if (e.target.value) {
                        addSchoolForEssays(e.target.value);
                        e.target.value = ""; // Reset dropdown
                      }
                    }}
                    className="styled-select"
                  >
                    <option value="">-- Select a school to add essays --</option>
                    {Object.entries(formData.collegeApplications)
                      .filter(([college, status]) => status === 'Applied' || status === 'Accepted' || status === 'Waitlisted' || status === 'Committed')
                      .filter(([college]) => !selectedSchools.includes(college))
                      .map(([college, status]) => (
                        <option key={college} value={college}>
                          {college} ({status})
                        </option>
                      ))}
                  </select>
                  
                  {selectedSchools.length > 0 && (
                    <div className="selected-schools-summary">
                      <h4>Schools with Essays:</h4>
                      <div className="selected-schools-list">
                        {selectedSchools.map(school => {
                          const essayCount = Object.keys(supplementalEssays).filter(key => key.startsWith(`${school}_`)).length;
                          return (
                            <div key={school} className="selected-school-item">
                              <div className="school-info">
                                <span className="school-name">{school}</span>
                                <span className="essay-count">({essayCount} essays)</span>
                              </div>
                              <div className="school-actions">
                                <button
                                  type="button"
                                  onClick={() => {
                                    // Capture current content before switching schools
                                    captureCurrentEssayContent();
                                    setActiveSupplementalSchool(school);
                                  }}
                                  className={`edit-button ${activeSupplementalSchool === school ? 'active' : ''}`}
                                >
                                  {activeSupplementalSchool === school ? 'Editing' : 'Edit Essays'}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => removeSchoolFromEssays(school)}
                                  className="remove-button"
                                  title={`Remove all ${school} essays`}
                                >
                                  Remove
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Essays for Active School */}
          {activeSupplementalSchool && (
            <div className="supplemental-essays-section">
              <div className="supplemental-essays-header">
                <h3 className="supplemental-essays-title">
                  {activeSupplementalSchool} Supplemental Essays
                </h3>
                <button
                  type="button"
                  onClick={() => removeSchoolFromEssays(activeSupplementalSchool)}
                  className="remove-school-link"
                >
                  Remove {activeSupplementalSchool} essays
                </button>
              </div>
              
              <p className="supplemental-info">
                Add up to 5 supplemental essays for {activeSupplementalSchool}. Include the prompt at the beginning of each essay if you wish.
              </p>
              
              {/* Always show 5 essay slots */}
              {Array(5).fill(null).map((_, index) => {
                const essayKey = `${activeSupplementalSchool}_essay_${index + 1}`;
                const currentWordCount = supplementalWordCounts[essayKey] || 0;
                
                return (
                  <div className="form-group supplemental-essay" key={essayKey}>
                    <label>
                      Supplemental Essay #{index + 1} for {activeSupplementalSchool}:
                    </label>
                    
                    <div className="redact-controls">
                      <button
                        type="button"
                        onClick={handleSupplementalRedactClick(activeSupplementalSchool, `essay_${index + 1}`)}
                        className="redact-button"
                        title="Redact selected text"
                      >
                        Redact Selection
                      </button>
                      <p className="redact-helper-text">
                        Select text to redact and click the button.
                      </p>
                    </div>
                    
                    <ReactQuill
                      key={essayKey}
                      ref={el => { if (el) supplementalRefs.current[essayKey] = el; }}
                      theme="snow"
                      value={supplementalEssays[essayKey] || ''}
                      onChange={handleSupplementalChange(activeSupplementalSchool, `essay_${index + 1}`)}
                      modules={quillModules}
                      formats={quillFormats}
                      placeholder={`Paste your supplemental essay #${index + 1} for ${activeSupplementalSchool}. You can include the prompt at the beginning if you wish.`}
                      className="quill-editor-container supplemental-editor"
                    />
                    
                    <div className={`word-count ${currentWordCount > 750 ? 'limit-exceeded' : ''}`}>
                      Word Count: {currentWordCount} / 750
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <button
            type="submit"
            className="submit-button"
            disabled={
              wordCount > MAX_WORDS || 
              Object.values(supplementalWordCounts).some(count => count > 750)
            }
            title={wordCount > MAX_WORDS ? `Essay exceeds ${MAX_WORDS} word limit` : 
                   (Object.values(supplementalWordCounts).some(count => count > 750) 
                    ? 'One or more supplemental essays exceed the word limit.' 
                    : "")}
          >
            {isEditMode 
              ? (selectedSchools.length > 0 ? `Update Profile with ${selectedSchools.length} School(s)` : 'Update Profile')
              : (selectedSchools.length > 0 ? `Submit Profile with ${selectedSchools.length} School(s)` : 'Submit Profile')
            }
          </button>
        </form>
      )}

      <style jsx global>{`
        .quill-editor-container .ql-editor {
          min-height: 200px;
          font-size: 1rem;
          line-height: 1.6;
          /* white-space: pre-wrap; */ /* Let Quill manage whitespace for paragraphs */
          background-color: var(--input-bg, #fff);
          color: var(--text-color, #333);
        }
        /* Add margin below paragraphs INSIDE the editor for visual spacing */
        .quill-editor-container .ql-editor p {
            margin-bottom: 1em; /* Adjust value as needed (e.g., 0.75em, 1.2em) */
        }
        .quill-editor-container .ql-toolbar.ql-snow {
            border: 1px solid var(--border-color, #ccc);
            border-top-left-radius: 4px;
            border-top-right-radius: 4px;
            background-color: var(--toolbar-bg, #f8f9fa);
        }
         .quill-editor-container .ql-container.ql-snow {
            border: 1px solid var(--border-color, #ccc);
            border-bottom-left-radius: 4px;
            border-bottom-right-radius: 4px;
            /* Remove top padding if margin-bottom on p is added */
            /* padding-top: 0; */
        }
         .quill-editor-container .ql-editor.ql-blank::before {
            color: var(--placeholder-color, #aaa);
            font-style: normal;
            left: 15px; right: 15px;
        }
        /* Smaller height for supplemental essays */
        .supplemental-editor .ql-editor {
          min-height: 150px;
        }
        
        /* Additional styles for supplemental essays */
        .supplemental-essays-section {
          margin-top: 2rem;
          padding-top: 1.5rem;
          border-top: 2px solid var(--border-color, #eee);
        }
        
        .supplemental-essays-title {
          margin-bottom: 1.5rem;
          font-size: 1.5rem;
          color: var(--text-darker, #111827);
          font-weight: 600;
        }
        
        .supplemental-info {
          font-size: 1rem;
          line-height: 1.5;
          color: var(--text-medium, #4b5563);
          margin-bottom: 1.5rem;
          padding: 0.75rem 1rem;
          background-color: var(--bg-neutral-alt, #f3f4f6);
          border-radius: 8px;
          border-left: 3px solid var(--accent-secondary, #f59e0b);
        }
        
        .supplemental-essay {
          margin-bottom: 2rem;
          padding-bottom: 2rem;
          border-bottom: 1px dashed var(--border-color-soft, #f3f4f6);
        }
        
        .supplemental-essay:last-child {
          border-bottom: none;
        }
        
        /* Multi-School Supplemental Essays Styles */
        .college-selection-for-essays {
          margin-top: 1rem;
        }
        
        .college-selection-for-essays label {
          display: block;
          margin-bottom: 0.75rem;
          font-weight: 600;
          color: var(--text-darker, #111827);
        }
        
        .selected-schools-summary {
          margin-top: 1.5rem;
          padding: 1.5rem;
          background: var(--bg-neutral-alt, #f8f9fa);
          border-radius: 8px;
          border-left: 3px solid var(--accent-color, #007bff);
        }
        
        .selected-schools-summary h4 {
          margin: 0 0 1rem 0;
          font-size: 1.1rem;
          color: var(--text-darker, #111827);
        }
        
        .selected-schools-list {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
        }
        
        .selected-school-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 0.75rem;
          background: white;
          border-radius: 6px;
          border: 1px solid var(--border-color, #e5e7eb);
        }
        
        .school-info {
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }
        
        .school-name {
          font-weight: 600;
          color: var(--text-darker, #111827);
        }
        
        .essay-count {
          font-size: 0.9rem;
          color: var(--text-medium, #6c757d);
        }
        
        .school-actions {
          display: flex;
          gap: 0.5rem;
        }
        
        .edit-button {
          padding: 0.375rem 0.75rem;
          border: 1px solid var(--accent-color, #007bff);
          background: white;
          color: var(--accent-color, #007bff);
          border-radius: 4px;
          cursor: pointer;
          font-size: 0.875rem;
          font-weight: 500;
          transition: all 0.2s ease;
        }
        
        .edit-button:hover {
          background: var(--accent-color, #007bff);
          color: white;
        }
        
        .edit-button.active {
          background: var(--accent-color, #007bff);
          color: white;
        }
        
        .remove-button {
          padding: 0.375rem 0.75rem;
          border: 1px solid #dc3545;
          background: white;
          color: #dc3545;
          border-radius: 4px;
          cursor: pointer;
          font-size: 0.875rem;
          font-weight: 500;
          transition: all 0.2s ease;
        }
        
        .remove-button:hover {
          background: #dc3545;
          color: white;
        }
        
        .supplemental-essays-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1rem;
        }
        
        .remove-school-link {
          background: none;
          border: none;
          color: #dc3545;
          cursor: pointer;
          font-size: 0.9rem;
          text-decoration: underline;
          padding: 0;
        }
        
        .remove-school-link:hover {
          color: #c82333;
        }
        
        /* Style for disabled college selector */
        select:disabled {
            background-color: #e9ecef; /* Lighter gray */
            cursor: not-allowed;
            opacity: 0.7;
        }
        .loading-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 3rem;
          text-align: center;
        }
        .loading-container .spinner {
          margin-bottom: 1rem;
        }
        .supplemental-helper {
          font-size: 0.9rem;
          color: var(--text-medium, #6c757d);
          margin: 0.5rem 0 1rem;
          line-height: 1.4;
        }
        /* Make the redact controls more compact for supplemental essays */
        .supplemental-essay .redact-controls {
          margin-bottom: 0.5rem;
        }
        .supplemental-essay .redact-helper-text {
          font-size: 0.75rem;
        }
      `}</style>
    </div>
  );
              }
